import { Check, Clock, AlertTriangle, ClipboardList, Euro, Download, Wind, SprayCan } from "lucide-react";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import CtaSection from "@/components/CtaSection";
import Seo from "@/components/Seo";

const METHOD_ICONS = { Wind, SprayCan };
const METHOD_TONES = {
    green: "bg-green-100 text-green-700",
    amber: "bg-amber-100 text-amber-700",
};

const ProtocoleLayout = ({ testid, tag, title, accent, intro, avant, apres, apresMethodes, attention, ctaTitle, ctaSubtitle, pdfUrl, seoTitle, seoDescription }) => (
    <div data-testid={testid}>
        <Seo title={seoTitle} description={seoDescription} />
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-16 pt-40 md:pt-48">
            <div className="absolute -right-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <MaskedLine delay={0.1}><SectionTag>{tag}</SectionTag></MaskedLine>
                <h1 className="mt-6 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl">
                    <MaskedLine delay={0.25}>{title}</MaskedLine>
                    <MaskedLine delay={0.4}>
                        <span className="text-brand">{accent}</span>
                    </MaskedLine>
                </h1>
                <MaskedLine delay={0.55}>
                    <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{intro}</p>
                </MaskedLine>
                {pdfUrl && (
                    <MaskedLine delay={0.7}>
                        <a
                            data-testid={`${testid}-download-button`}
                            href={pdfUrl}
                            download
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                        >
                            <Download className="h-5 w-5" />
                            Télécharger le protocole en PDF
                        </a>
                    </MaskedLine>
                )}
            </div>
        </section>

        {/* AVANT */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal><SectionTag>Avant le traitement</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Préparer votre <span className="text-brand">logement</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-8 lg:grid-cols-2">
                    {avant.map((step, i) => (
                        <Reveal key={step.num} delay={0.08 * i} className={i === avant.length - 1 && avant.length % 2 ? "lg:col-span-2" : ""}>
                            <div
                                data-testid={`${testid}-step-${step.num}`}
                                className="group relative h-full overflow-hidden rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/50 sm:p-10"
                            >
                                <span className="absolute -right-4 -top-8 font-display text-[8rem] font-black leading-none text-night/5 transition-colors duration-500 group-hover:text-brand/15">
                                    {step.num}
                                </span>
                                <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand">Étape {step.num}</p>
                                <h3 className="mt-3 font-display text-2xl font-black uppercase tracking-tight">{step.title}</h3>
                                <ul className="mt-6 space-y-3">
                                    {step.items.map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* APRÈS */}
        <section className="bg-mist py-20 md:py-24">
            <div className={`mx-auto px-5 sm:px-8 ${apresMethodes ? "max-w-7xl" : "max-w-5xl"}`}>
                <Reveal><SectionTag>Après le traitement</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        À votre <span className="text-brand">retour</span>
                    </h2>
                </Reveal>
                {apresMethodes ? (
                    <>
                        <Reveal delay={0.15}>
                            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
                                Les consignes diffèrent selon la méthode utilisée : suivez la colonne correspondant à votre traitement.
                            </p>
                        </Reveal>
                        <div className="mt-14 grid gap-8 lg:grid-cols-2">
                            {apresMethodes.map((m, i) => {
                                const MethodIcon = METHOD_ICONS[m.icon] || Clock;
                                return (
                                    <Reveal key={m.method} delay={0.1 * i} className="h-full">
                                        <div data-testid={`${testid}-apres-${i === 0 ? "vapeur" : "chimique"}`} className="h-full rounded-3xl border border-black/10 bg-white p-8 shadow-sm sm:p-10">
                                            <div className="flex items-center gap-4">
                                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                                    <MethodIcon className="h-6 w-6" />
                                                </div>
                                                <div>
                                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest ${METHOD_TONES[m.tone] || "bg-brand/10 text-brand"}`}>
                                                        {m.badge}
                                                    </span>
                                                    <h3 className="mt-2 font-display text-2xl font-black uppercase tracking-tight">{m.method}</h3>
                                                </div>
                                            </div>
                                            <ul className="mt-6 space-y-3">
                                                {m.items.map((item) => (
                                                    <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div className="mt-14 space-y-4" data-testid={`${testid}-apres-list`}>
                        {apres.map((item, i) => (
                            <Reveal key={i} delay={0.05 * i}>
                                <div className="flex items-start gap-4 rounded-2xl border border-black/10 bg-white p-6 shadow-sm">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                                        <Clock className="h-5 w-5" />
                                    </div>
                                    <p className="text-sm leading-relaxed text-slate-700 sm:text-base">{item}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>

        {/* ATTENTION */}
        <section className="bg-white py-20 md:py-24">
            <div className="mx-auto max-w-5xl px-5 sm:px-8">
                <Reveal><SectionTag>Attention</SectionTag></Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        Points de <span className="text-brand">vigilance</span>
                    </h2>
                </Reveal>
                <div className="mt-14 space-y-6" data-testid={`${testid}-attention`}>
                    {attention.map((block, i) => (
                        <Reveal key={block.title} delay={0.08 * i}>
                            <div className="flex items-start gap-4 rounded-3xl border border-amber-500/40 bg-amber-50 p-8">
                                <AlertTriangle className="mt-1 h-6 w-6 shrink-0 text-amber-500" />
                                <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
                                    <strong className="font-display">{block.title} :</strong> {block.text}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                    <Reveal delay={0.2}>
                        <div data-testid={`${testid}-fee-warning`} className="flex items-center justify-between gap-6 rounded-3xl border border-red-500/30 bg-red-50 p-8">
                            <div className="flex items-start gap-4">
                                <ClipboardList className="mt-1 h-6 w-6 shrink-0 text-red-500" />
                                <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
                                    Si vous êtes absent au rendez-vous fixé ou si le logement n'est pas correctement préparé,
                                    le logement ne sera pas traité et un déplacement vous sera facturé.
                                </p>
                            </div>
                            <div className="hidden shrink-0 items-center gap-1 rounded-2xl bg-red-500/10 px-6 py-4 sm:flex">
                                <Euro className="h-5 w-5 text-red-500" />
                                <span className="font-display text-3xl font-black text-red-500">59,00</span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        <CtaSection title={ctaTitle} subtitle={ctaSubtitle} />
    </div>
);

export default ProtocoleLayout;
